export const USER_TIERS = Object.freeze({
    FREE: 'FREE',
    FREEMIUM: 'FREEMIUM',
    PREMIUM: 'PREMIUM'
});

export const FEATURE_KEYS = Object.freeze({
    MANUAL_ENTRY: 'manualEntry',
    COPY_PASTE_IMPORT: 'copyPasteImport',
    TEST_MODE: 'testMode',
    MATCH_MODE: 'matchMode',
    DARK_MODE: 'darkMode',
    BASIC_STREAKS: 'basicStreaks',
    EXCEL_UPLOAD: 'excelUpload',
    AI_LIST_GENERATION: 'aiListGeneration',
    BASIC_DASHBOARD: 'basicDashboard',
    PHOTO_SCAN_TO_QUIZ: 'photoScanToQuiz',
    AI_OPEN_ANSWER_VALIDATION: 'aiOpenAnswerValidation',
    AD_FREE: 'adFree',
    BLITZ_MODE: 'blitzMode',
    FULL_GAMIFICATION: 'fullGamification',
    PARENT_TEACHER_DASHBOARDS: 'parentTeacherDashboards'
});

export const QUOTA_KEYS = Object.freeze({
    LISTS: 'lists',
    AI_LIST_GENERATIONS: 'aiListGenerations',
    PHOTO_SCANS: 'photoScans',
    AI_ANSWER_VALIDATIONS: 'aiAnswerValidations'
});

const TIER_RANK = Object.freeze({
    [USER_TIERS.FREE]: 0,
    [USER_TIERS.FREEMIUM]: 1,
    [USER_TIERS.PREMIUM]: 2
});

export const DEFAULT_QUOTAS_BY_TIER = Object.freeze({
    [USER_TIERS.FREE]: {
        [QUOTA_KEYS.LISTS]: { used: 0, limit: 5, period: 'lifetime', resetsAt: null },
        [QUOTA_KEYS.AI_LIST_GENERATIONS]: { used: 0, limit: 0, period: 'monthly', resetsAt: null },
        [QUOTA_KEYS.PHOTO_SCANS]: { used: 0, limit: 0, period: 'monthly', resetsAt: null },
        [QUOTA_KEYS.AI_ANSWER_VALIDATIONS]: { used: 0, limit: 0, period: 'monthly', resetsAt: null }
    },
    [USER_TIERS.FREEMIUM]: {
        [QUOTA_KEYS.LISTS]: { used: 0, limit: null, period: 'lifetime', resetsAt: null },
        [QUOTA_KEYS.AI_LIST_GENERATIONS]: { used: 0, limit: 3, period: 'monthly', resetsAt: null },
        [QUOTA_KEYS.PHOTO_SCANS]: { used: 0, limit: 0, period: 'monthly', resetsAt: null },
        [QUOTA_KEYS.AI_ANSWER_VALIDATIONS]: { used: 0, limit: 0, period: 'monthly', resetsAt: null }
    },
    [USER_TIERS.PREMIUM]: {
        [QUOTA_KEYS.LISTS]: { used: 0, limit: null, period: 'lifetime', resetsAt: null },
        [QUOTA_KEYS.AI_LIST_GENERATIONS]: { used: 0, limit: null, period: 'monthly', resetsAt: null },
        [QUOTA_KEYS.PHOTO_SCANS]: { used: 0, limit: null, period: 'monthly', resetsAt: null },
        [QUOTA_KEYS.AI_ANSWER_VALIDATIONS]: { used: 0, limit: null, period: 'monthly', resetsAt: null }
    }
});

export const FEATURE_ACCESS = Object.freeze({
    [FEATURE_KEYS.MANUAL_ENTRY]: {
        label: 'Manual entry',
        requiredTier: USER_TIERS.FREE
    },
    [FEATURE_KEYS.COPY_PASTE_IMPORT]: {
        label: 'Copy-paste import',
        requiredTier: USER_TIERS.FREE
    },
    [FEATURE_KEYS.TEST_MODE]: {
        label: 'Test mode',
        requiredTier: USER_TIERS.FREE
    },
    [FEATURE_KEYS.MATCH_MODE]: {
        label: 'Match mode',
        requiredTier: USER_TIERS.FREE
    },
    [FEATURE_KEYS.DARK_MODE]: {
        label: 'Dark mode',
        requiredTier: USER_TIERS.FREE
    },
    [FEATURE_KEYS.BASIC_STREAKS]: {
        label: 'Basic streaks',
        requiredTier: USER_TIERS.FREE
    },
    [FEATURE_KEYS.EXCEL_UPLOAD]: {
        label: 'Excel upload',
        requiredTier: USER_TIERS.FREEMIUM
    },
    [FEATURE_KEYS.AI_LIST_GENERATION]: {
        label: 'AI list generation',
        requiredTier: USER_TIERS.FREEMIUM,
        quotaKey: QUOTA_KEYS.AI_LIST_GENERATIONS
    },
    [FEATURE_KEYS.BASIC_DASHBOARD]: {
        label: 'Basic dashboard',
        requiredTier: USER_TIERS.FREEMIUM
    },
    [FEATURE_KEYS.PHOTO_SCAN_TO_QUIZ]: {
        label: 'Photo/Document Scan to Quiz',
        requiredTier: USER_TIERS.PREMIUM,
        quotaKey: QUOTA_KEYS.PHOTO_SCANS,
        paywallTitle: 'Scan pages into quizzes'
    },
    [FEATURE_KEYS.AI_OPEN_ANSWER_VALIDATION]: {
        label: 'AI Open Answer Validation',
        requiredTier: USER_TIERS.PREMIUM,
        quotaKey: QUOTA_KEYS.AI_ANSWER_VALIDATIONS,
        paywallTitle: 'Practice open answers with AI feedback'
    },
    [FEATURE_KEYS.AD_FREE]: {
        label: 'No ads',
        requiredTier: USER_TIERS.PREMIUM
    },
    [FEATURE_KEYS.BLITZ_MODE]: {
        label: 'Blitz mode',
        requiredTier: USER_TIERS.PREMIUM
    },
    [FEATURE_KEYS.FULL_GAMIFICATION]: {
        label: 'Full gamification',
        requiredTier: USER_TIERS.PREMIUM
    },
    [FEATURE_KEYS.PARENT_TEACHER_DASHBOARDS]: {
        label: 'Parent/teacher dashboards',
        requiredTier: USER_TIERS.PREMIUM
    }
});

export function normalizeTier(tier) {
    return Object.prototype.hasOwnProperty.call(TIER_RANK, tier) ? tier : USER_TIERS.FREE;
}

export function tierMeets(currentTier, requiredTier) {
    return TIER_RANK[normalizeTier(currentTier)] >= TIER_RANK[normalizeTier(requiredTier)];
}

export function createDefaultSubscription(now = new Date()) {
    return {
        tier: USER_TIERS.FREE,
        status: 'active',
        provider: 'internal',
        planId: 'free',
        startedAt: now,
        currentPeriodStart: now,
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
        updatedAt: now
    };
}

export function mergeTierQuotas(tier, storedQuotas = {}) {
    const normalizedTier = normalizeTier(tier);
    const tierDefaults = DEFAULT_QUOTAS_BY_TIER[normalizedTier];

    return Object.keys(tierDefaults).reduce((acc, quotaKey) => {
        acc[quotaKey] = {
            ...tierDefaults[quotaKey],
            ...(storedQuotas?.[quotaKey] || {})
        };
        return acc;
    }, {});
}

export function getFeatureGate(featureKey, options = {}) {
    const feature = FEATURE_ACCESS[featureKey];

    if (!feature) {
        return {
            allowed: false,
            reason: 'unknown_feature',
            featureKey,
            feature: null
        };
    }

    const tier = normalizeTier(options.tier);
    const override = options.featureOverrides?.[featureKey];
    const forcedAllow = override === true;

    if (override === false) {
        return {
            allowed: false,
            reason: 'disabled_by_override',
            featureKey,
            feature,
            tier
        };
    }

    if (!forcedAllow && !tierMeets(tier, feature.requiredTier)) {
        return {
            allowed: false,
            reason: 'tier_required',
            requiredTier: feature.requiredTier,
            featureKey,
            feature,
            tier
        };
    }

    const quota = !forcedAllow && feature.quotaKey ? options.quotas?.[feature.quotaKey] : null;
    const quotaExhausted = quota && quota.limit !== null && Number(quota.used || 0) >= Number(quota.limit || 0);

    if (quotaExhausted) {
        return {
            allowed: false,
            reason: 'quota_exhausted',
            requiredTier: feature.requiredTier,
            featureKey,
            feature,
            tier,
            quota
        };
    }

    return {
        allowed: true,
        reason: forcedAllow ? 'allowed_by_override' : 'allowed',
        requiredTier: feature.requiredTier,
        featureKey,
        feature,
        tier,
        quota
    };
}
